import {Stack} from '@commercial-helios/ui/src/components/stack'
import {tokens, colors} from '@commercial-helios/ui/theme'
import type {ReactNode} from 'react'
import React from 'react'
import styled from 'styled-components'

import {useSteps} from '@/components/step-wizard'
import Button from '@helios/shared/components/button'
import {Text, NavTextLink} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

import type {Data} from './expense-policy-builder'

interface Props {
    disableNext?: boolean
    title: string
    subtitle?: string
    hideControls?: boolean
    onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
    onSkip?: () => void
    togglePreview?: () => void
    data: Data['sidebar'][number]
    children: ReactNode
}

const Sidebar = ({
    children,
    disableNext,
    title,
    subtitle,
    hideControls,
    onSubmit,
    onSkip,
    togglePreview,
    data,
}: Props) => {
    const {navigate, activeStepIndex, totalSteps} = useSteps()

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        onSubmit?.(e)
        navigate.next()
    }

    const handleSkip = () => {
        onSkip?.()
        navigate.next()
    }

    const stepCountOffsetStart = 1
    const stepCountOffsetEnd = 1

    return (
        <Form onSubmit={handleSubmit}>
            <ScrollableContainer>
                <Wrapper>
                    <Header>
                        <Prev variant="title-base" onClick={navigate.prev}>
                            ←
                        </Prev>

                        <PreviewBtn onClick={togglePreview}>{data.preview}</PreviewBtn>

                        {!hideControls && (
                            <Text variant="label-base" color="muted">
                                {data.step} {activeStepIndex + 1 - stepCountOffsetStart} {data.of}{' '}
                                {totalSteps - stepCountOffsetStart - stepCountOffsetEnd}
                            </Text>
                        )}
                    </Header>

                    <Stack space={10}>
                        <Text as="h1" variant="title-base">
                            {title}
                        </Text>

                        {subtitle && (
                            <Text variant="paragraph-base" color="muted">
                                {subtitle}
                            </Text>
                        )}
                    </Stack>

                    <Stack space={32}>{children}</Stack>
                </Wrapper>
            </ScrollableContainer>

            {!hideControls && (
                <Controls>
                    <NavTextLink
                        as="button"
                        type="button"
                        color="brand"
                        variant="label-base"
                        onClick={handleSkip}
                    >
                        {data.skip}
                    </NavTextLink>

                    <Button type="submit" disabled={disableNext}>
                        {data.next}
                    </Button>
                </Controls>
            )}
        </Form>
    )
}

const Form = styled.form`
    display: grid;
    min-height: 100vh;
    max-height: 100vh;
    grid-template-rows: 1fr auto;

    ${mq.max('xs')} {
        min-height: -webkit-fill-available;
        max-height: -webkit-fill-available;
    }
`

const ScrollableContainer = styled.div`
    overflow: auto;
`

const Wrapper = styled.div`
    display: grid;
    width: 100%;
    min-height: 100%;
    padding: ${tokens.spacing24};
    background: ${colors.background.default};
    grid-template-rows: auto auto 1fr;
    row-gap: ${tokens.spacing32};

    ${mq.min('lg')} {
        padding: ${tokens.spacing40};
    }
`

const Header = styled.div`
    display: grid;
    width: 100%;
    grid-template-columns: auto auto;
    justify-content: space-between;
    justify-items: start;
    align-items: center;
    row-gap: ${tokens.spacing24};
`

const Prev = styled(Text).attrs({as: 'button', type: 'button'})`
    padding: 0;
    cursor: pointer;
    background: none;
    border: none;
`

const Controls = styled.footer`
    position: relative;
    bottom: 0;
    display: grid;
    padding: ${tokens.spacing12} ${tokens.spacing40};
    background: ${colors.background.default};
    box-shadow: ${tokens.shadowRaiseSubtle};
    grid-template-columns: repeat(2, auto);
    justify-content: end;
    column-gap: ${tokens.spacing40};
`

const PreviewBtn = styled(NavTextLink)`
    ${mq.min('md')} {
        display: none;
    }
`

export default Sidebar
